/**
 * NFC Solutions — Release status panel. Session 30.28.
 *
 * Administration → App downloads. Shows, per platform, what is published
 * and what the LAST GitHub build did (building / published / skipped /
 * failed) with the reason and a link to the run — so the admin never has
 * to open GitHub to know whether the last "Save to GitHub" shipped.
 *
 * Data: GET /api/admin/releases/status (admin session). The GitHub
 * workflows feed it via POST /api/ci/builds/report at start and end.
 * Polls every 20 s while mounted (builds take 5–15 minutes).
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2, XCircle, Loader2, AlertTriangle, ExternalLink, RefreshCw,
  Smartphone, Monitor, GitCommitHorizontal, Clock3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { apiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const POLL_MS = 20_000;

const STATUS_META = {
  building: { label: "Building", Icon: Loader2, spin: true, className: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300" },
  published: { label: "Published", Icon: CheckCircle2, className: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" },
  skipped: { label: "Not published", Icon: AlertTriangle, className: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300" },
  failed: { label: "Failed", Icon: XCircle, className: "border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
};

const StatusBadge = ({ status, testId }) => {
  const meta = STATUS_META[status] || { label: "No builds yet", Icon: Clock3, className: "border-border bg-secondary text-muted-foreground" };
  const Icon = meta.Icon;
  return (
    <Badge className={`gap-1 rounded-full border text-[10px] ${meta.className}`} data-testid={testId}>
      <Icon className={`h-3 w-3 ${meta.spin ? "animate-spin" : ""}`} />
      {meta.label}
    </Badge>
  );
};

export const ReleaseStatusPanel = ({ token, active = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!token) return;
    if (!silent) setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/releases/status`, { headers: { Authorization: `Bearer ${token}` } });
      setData(r.data);
      setError("");
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load release status."));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!active) return undefined;
    load();
    const id = setInterval(() => load(true), POLL_MS);
    return () => clearInterval(id);
  }, [active, load]);

  const platforms = data?.platforms || {};
  const anyBuilding = Object.values(platforms).some((p) => p?.last_build?.status === "building");

  return (
    <div className="rounded-2xl border border-border bg-secondary/30 p-4" data-testid="admin-release-status-panel">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold">
            Release status
            {anyBuilding ? <Loader2 className="h-3.5 w-3.5 animate-spin text-sky-600" /> : null}
          </p>
          <p className="text-xs text-muted-foreground">
            What the last GitHub build did. Every save to GitHub becomes the next version automatically.
            {data?.last_allocation?.version ? ` Latest version number handed out: v${data.last_allocation.version}.` : ""}
          </p>
        </div>
        <Button variant="outline" size="sm" className="h-8 rounded-lg" onClick={() => load()} disabled={loading} data-testid="admin-release-status-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {error ? (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-700 dark:text-rose-300" data-testid="admin-release-status-error">
          {error} <button type="button" className="underline" onClick={() => load()}>Retry</button>
        </div>
      ) : null}

      {data && data.ci_enabled === false ? (
        <div className="mb-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-800 dark:text-amber-200" data-testid="admin-release-status-ci-disabled">
          This backend has no <code>NFCS_CI_UPLOAD_TOKEN</code>, so GitHub cannot publish builds or report here. Set it in the deployed backend environment and add the same value as a GitHub secret.
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2">
        {["apk", "exe"].map((platform) => {
          const p = platforms[platform];
          const build = p?.last_build;
          const pub = p?.published;
          const PlatformIcon = platform === "apk" ? Smartphone : Monitor;
          return (
            <div key={platform} className="flex flex-col gap-2 rounded-xl border border-border bg-background p-3" data-testid={`admin-release-card-${platform}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${platform === "apk" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" : "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"}`}>
                    <PlatformIcon className="h-4 w-4" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold">{platform === "apk" ? "Android APK" : "Windows EXE"}</p>
                    <p className="text-[11px] text-muted-foreground" data-testid={`admin-release-published-${platform}`}>
                      {loading && !data ? <Skeleton className="h-3 w-28" /> : pub?.version
                        ? `Live: v${pub.version} · ${timeAgo(pub.uploaded_at)}${pub.uploaded_by === "ci" ? " · from GitHub" : pub.uploaded_by ? " · manual upload" : ""}`
                        : "Nothing published yet"}
                    </p>
                  </div>
                </div>
                {loading && !data ? <Skeleton className="h-5 w-20 rounded-full" /> : <StatusBadge status={build?.status} testId={`admin-release-status-${platform}`} />}
              </div>

              {build ? (
                <div className="rounded-lg bg-secondary/50 p-2.5 text-xs" data-testid={`admin-release-last-build-${platform}`}>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-foreground">
                    <span className="font-medium">
                      {build.status === "building" ? "Building" : "Last build"} {build.version ? `v${build.version}` : ""}
                    </span>
                    {build.sha ? (
                      <span className="inline-flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
                        <GitCommitHorizontal className="h-3 w-3" /> {build.sha}
                      </span>
                    ) : null}
                    <span className="text-muted-foreground">· {timeAgo(build.updated_at)}</span>
                  </div>
                  {build.note ? (
                    <p className={`mt-1 leading-snug ${build.status === "failed" ? "text-rose-700 dark:text-rose-300" : build.status === "skipped" ? "text-amber-800 dark:text-amber-200" : "text-muted-foreground"}`} data-testid={`admin-release-note-${platform}`}>
                      {build.note}
                    </p>
                  ) : null}
                  {build.run_url ? (
                    <a
                      href={build.run_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-primary underline-offset-2 hover:underline"
                      data-testid={`admin-release-run-link-${platform}`}
                    >
                      View run on GitHub <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                </div>
              ) : !loading ? (
                <p className="text-xs text-muted-foreground" data-testid={`admin-release-empty-${platform}`}>
                  No GitHub builds reported yet. Save to GitHub and this will fill in within a minute.
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};
